import { useEffect, useState } from "react";
import axios from "axios";
import { CARD, CARD2, CARD3, BD, TX, TX2, GR, BL, YL, RD, PU } from "../../utils/constants";
import { isNum } from "../../utils/formatUtils";

const API = process.env.REACT_APP_API_URL || "";
const REFRESH_MS = 3 * 60 * 1000;

const CATEGORY = {
  index:      { label: "INDEX",      glyph: "▤", color: BL },
  volatility: { label: "VOLATILITY", glyph: "⚡", color: YL },
  sector:     { label: "SECTOR",     glyph: "◈", color: PU },
  stock:      { label: "STOCK",      glyph: "●", color: GR },
  earnings:   { label: "EARNINGS",   glyph: "◆", color: YL },
  economic:   { label: "ECONOMIC",   glyph: "▲", color: TX2 },
};

/** "just now" / "4 min ago" — so freshness is visible rather than assumed. */
function relativeTime(ms) {
  if (!isNum(ms)) return null;
  const mins = Math.floor((Date.now() - ms) / 60000);
  if (mins < 1) return "just now";
  if (mins === 1) return "1 min ago";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  return hrs === 1 ? "1 hour ago" : `${hrs} hours ago`;
}

/**
 * The developments that actually moved markets since the previous session.
 *
 * Refreshes on its own timer, independent of the manual scan — the scan is an
 * expensive 200+ symbol run, and tying this to it would leave the panel stale
 * for hours while the page still claimed to be live. Its own timestamp is
 * shown for the same reason.
 *
 * Ranking is server-side, by how unusual each move is for that specific asset,
 * so a 0.8% index session can outrank a 3% move in a stock that swings that
 * much routinely.
 */
export default function MarketChanges() {
  const [state, setState] = useState({ loading: true, changes: [], note: null, error: null, asOf: null });
  const [, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/api/market-changes`, { timeout: 60000 });
        if (cancelled) return;
        setState({
          loading: false,
          changes: r.data?.changes || [],
          note: r.data?.note || null,
          error: null,
          asOf: r.data?.asOf ?? Date.now(),
        });
      } catch {
        if (!cancelled) setState((p) => ({ ...p, loading: false, error: "Could not load market changes." }));
      }
    };
    load();
    const refresh = setInterval(load, REFRESH_MS);
    // Re-render once a minute so the "x min ago" label stays truthful.
    const ticker = setInterval(() => setTick((t) => t + 1), 60000);
    return () => { cancelled = true; clearInterval(refresh); clearInterval(ticker); };
  }, []);

  const { loading, changes, note, error, asOf } = state;
  const maxSig = changes.reduce((m, c) => Math.max(m, c.significance || 0), 0) || 1;
  const freshness = relativeTime(asOf);

  return (
    <div className="ts-panel" style={{ background: CARD, border: `1px solid ${BD}`, borderRadius: 12, padding: "15px 18px 13px", marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 3 }}>
        <div style={{ fontSize: 11, color: TX, letterSpacing: 2, fontWeight: 800, fontFamily: "'Space Grotesk',sans-serif" }}>
          BIGGEST MARKET CHANGES — 24H
        </div>
        <div style={{ fontSize: 8, color: TX2, display: "flex", alignItems: "center", gap: 6 }}>
          {freshness && (
            <>
              <span style={{ width: 5, height: 5, borderRadius: "50%", background: GR, display: "inline-block" }} />
              Updated {freshness}
            </>
          )}
        </div>
      </div>
      <div style={{ fontSize: 8, color: TX2, marginBottom: 12 }}>
        Measured vs previous session close · ranked by how unusual each move is for that asset
      </div>

      {loading && <div style={{ fontSize: 9, color: TX2, padding: "8px 0" }}>Loading market developments…</div>}
      {error && <div style={{ fontSize: 9, color: RD, padding: "8px 0" }}>{error}</div>}
      {!loading && !error && changes.length === 0 && (
        <div style={{ fontSize: 9, color: TX2, padding: "8px 0" }}>{note || "No standout moves since the previous session."}</div>
      )}

      {changes.map((c, i) => {
        const cat = CATEGORY[c.category] || { label: (c.category || "MARKET").toUpperCase(), glyph: "•", color: TX2 };
        const up = isNum(c.changePct) && c.changePct >= 0;
        const dirColor = !isNum(c.changePct) ? TX2 : up ? GR : RD;
        const sigPct = Math.max(6, Math.round(((c.significance || 0) / maxSig) * 100));

        return (
          <div
            key={`${c.category}-${c.symbol}-${i}`}
            style={{
              display: "flex", gap: 11, alignItems: "center",
              background: CARD2, borderRadius: 8, padding: "9px 12px", marginBottom: 6,
              borderLeft: `3px solid ${dirColor}`,
            }}
          >
            <div style={{ fontSize: 9, color: TX2, fontWeight: 700, minWidth: 12, textAlign: "center" }}>{i + 1}</div>

            <div style={{
              width: 26, height: 26, borderRadius: 7, flexShrink: 0,
              background: `${cat.color}14`, border: `1px solid ${cat.color}33`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: cat.color, fontSize: 11,
            }}>
              {cat.glyph}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 7, flexWrap: "wrap" }}>
                <span style={{ fontSize: 7, color: cat.color, letterSpacing: 1.4, fontWeight: 700 }}>{cat.label}</span>
                <span style={{ fontSize: 10.5, color: TX, fontWeight: 600 }}>{c.headline}</span>
              </div>
              {c.detail && <div style={{ fontSize: 8, color: TX2, marginTop: 2 }}>{c.detail}</div>}
              {/* Visual weight of the ranking, so the ordering is legible at a glance */}
              <div style={{ height: 2, background: CARD3, borderRadius: 2, marginTop: 6, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${sigPct}%`, background: `${cat.color}80`, borderRadius: 2 }} />
              </div>
            </div>

            {isNum(c.changePct) && (
              <div style={{ textAlign: "right", minWidth: 62 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: dirColor, fontFamily: "'Space Grotesk',sans-serif", lineHeight: 1 }}>
                  {up ? "▲" : "▼"} {Math.abs(c.changePct).toFixed(2)}%
                </div>
                {c.symbol && <div style={{ fontSize: 7, color: TX2, marginTop: 3 }}>{c.symbol}</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
